import React, { useEffect, useRef, useState, useContext } from 'react';
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { Room, Track, RoomEvent } from 'livekit-client';
import { useRecoilState } from "recoil";
import canAutoPlay from 'can-autoplay';
import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next';
import cn from 'classnames'

//import assets
import { ReactComponent as AssembloLogoHor } from "../assets/img/assemblo-logo-hor.svg";

//import services
import MeetingService from "../services/meeting.service";
import ChatService from "../services/chat.service";
import { socket, SocketContext } from "../services/socket.service";
import VotingService from '../services/voting.service';

//import components
import ChatComponent from "../components/chat.component";
import EndedComponent from "../components/ended.component";
import WaitingComponent from "../components/waiting.component";
import Spinner from "../components/ui/spinner.component";


//import datastores
import { meState } from "../data/me.atom";
import { meetingConnectionState } from "../data/meeting.atom";
import Toggle from '../components/ui/toggle.component';

function MeetingViewContent() {
    const { t } = useTranslation();
    const [me] = useRecoilState(meState);
    const [connectionState, setConnectionState] = useRecoilState(meetingConnectionState);

    const mainPlayer = useRef();
    const subPlayer = useRef();
    const audioPlayer = useRef(); // Dedicated audio element for language tracks
    const mainPlayerHandlerRef = useRef(null);
    const subPlayerHandlerRef = useRef(null);
    const isConnectingRef = useRef(false);

    const [lang, setLang] = useState("NL");
    const [subPlayerVisible, setSubPlayerVisible] = useState(true);

    const [status, setStatus] = useState("not started");
    const [chat, setChat] = useState([]);
    const [message, setMessage] = useState("");
    const [meeting, setMeeting] = useState({});
    const [tracks, setTracks] = useState([]);
    const roomRef = useRef(null);

    const [voting, setVoting] = useState({});
    const [votingSelect, setVotingSelect] = useState("");
    const [globalVotes, setGlobalVotes] = useState({ "yes": 0, "no": 0, "abstain": 0 });


    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const socketprovider = useContext(SocketContext);
    const [autoplay, setAutoplay] = useState(false);
    const [hasCheckedAtom, setHasCheckedAtom] = useState(false);
    const [isValidConnection, setIsValidConnection] = useState(false);
    const [isCheckingConnection, setIsCheckingConnection] = useState(true);

    const [mainPlaceholder, setMainPlaceholder] = useState(true);
    const [subPlaceholder, setSubPlaceholder] = useState(true);
    const [isVideoConnecting, setIsVideoConnecting] = useState(false);
    const [isSubVideoConnecting, setIsSubVideoConnecting] = useState(false);

    const [open, setOpen] = useState(false)

    //use effect to define language and check atom
    useEffect(() => {
        if (searchParams.get("lang")) {
            if (lang.toLowerCase() != searchParams.get("lang")) {
                setLang(searchParams.get("lang").toUpperCase())
            }
        }
        
        // Check atom after component mounts
        if (!hasCheckedAtom) {
            setHasCheckedAtom(true);
            
            // If atom is empty and we're not coming from home page with connection data
            if (!connectionState.url || !connectionState.token) {
                // Give a moment for atom to populate if coming from home page
                setTimeout(() => {
                    if (!connectionState.url || !connectionState.token) {
                        setIsCheckingConnection(false);
                        navigate('/');
                        return;
                    } else {
                        setIsValidConnection(true);
                        setIsCheckingConnection(false);
                    }
                }, 500);
            } else {
                setIsValidConnection(true);
                setIsCheckingConnection(false);
            }
        }

        // Don't auto-check autoplay - require user to explicitly click "Follow" button
        // reloadAutoplay();
    }, [connectionState, hasCheckedAtom, navigate])

    // Handle when connection state changes (e.g., when atom gets populated)
    useEffect(() => {
        if (hasCheckedAtom && !isValidConnection && (connectionState.url && connectionState.token)) {
            setIsValidConnection(true);
            setIsCheckingConnection(false);
        }
    }, [connectionState, hasCheckedAtom, isValidConnection])

    // Show popup when meeting starts (requires user to click "Follow" button)
    useEffect(() => {
        if (status === "started" && !autoplay) {
            setOpen(true);
        }
    }, [status, autoplay])

    //use effect to load meeting
    useEffect(() => {
        MeetingService.getMeeting(id)
            .then(response => {
                setMeeting(response.data);
                document.title = response.data.title;
            })
            .catch(err => {
                toast.error(err.response.data.message);
            });
    }, []);

    useEffect(() => {
        MeetingService.status(id)
            .then(response => {
                setStatus(response.data);
            })
            .catch(err => {
            }
            );

        ChatService.getChats(id)
            .then(response => {
                setChat(response.data);
            }
            );

        VotingService.getVoting(id)
            .then(response => {
                setVoting(response.data);
                VotingService.getAllVotes(response.data.id)
                    .then(response => {
                        setGlobalVotes(response.data);
                    }
                    );
                if (response.data != null) {
                    VotingService.getVote(response.data.id)
                        .then(response => {
                            if (response.data.response != null) {
                                setVotingSelect(response.data.response);
                            }
                        })
                        .catch(err => {
                                    }
                        );
                }
            }
            );
    }, []);



    useEffect(() => {
        if (id && connectionState.live_token) {
            socketprovider.connect(id, connectionState.live_token);
            socketprovider.socket.on('meeting', function (data) {
                if (data.id == id && data.status == "start") {
                    setStatus("started");
                }
                if (data.id == id && data.status == "end") {
                    setStatus("ended")
                }
            });

            socketprovider.socket.on('chat', function (data) {
                if (data.meeting == id) {
                    setChat(chat => [...chat, { user: data.user, message: data.message, created_on: data.created_on }]);
                }
            });

            socketprovider.socket.on('question', function (data) {
                if (data.meeting_id == id) {
                    setVotingSelect("");
                    setVoting(data);
                }
            });

            socketprovider.socket.on('refresh', function (data) {
                if (data.user_id == me.id) {
                    window.location.reload();
                }
            });

            socketprovider.socket.on('votes', function (data) {
                if (data.meeting_id == id) {
                    setGlobalVotes({
                        yes: data.yes,
                        no: data.no,
                        abstain: data.abstain
                    })
                }
            });


            return () => {
                socketprovider.socket.off('meeting');
                socketprovider.socket.off('chat');
                socketprovider.socket.off('question');
                socketprovider.socket.off('refresh');
                socketprovider.socket.off('votes');
                socketprovider.disconnect();
            }
        }
    }, [id, connectionState.live_token]);

    // Initialize and manage LiveKit room
    useEffect(() => {
        let room = null;

        const initializeRoom = () => {
            if (room) return room;
            
            room = new Room({
                adaptiveStream: true,
                dynacast: true,
                videoCaptureDefaults: {
                    resolution: {
                        width: 1280,
                        height: 720,
                        frameRate: 30,
                    },
                },
            });

            // Selective subscription for bandwidth optimization
            const handleTrackPublished = (publication, participant) => {

                let shouldSubscribe = false;

                if (publication.kind === 'video') {
                    // Subscribe to all video (OBS + main camera)
                    shouldSubscribe = true;
                } else if (publication.kind === 'audio') {
                    const trackName = publication.name || publication.trackName || '';

                    if (participant.identity === `whip_streamer_${id}`) {
                        // Always subscribe to OBS audio
                        shouldSubscribe = true;
                    } else {
                        // Language-specific audio tracks (nl, fr, en)
                        const trackLang = trackName.toLowerCase();
                        const selectedLang = lang.toLowerCase();

                        // Match track names like "dutch_audio" with "nl", "french_audio" with "fr"
                        shouldSubscribe = trackLang === selectedLang ||
                                        trackLang.startsWith(selectedLang) ||
                                        (selectedLang === "nl" && trackLang.includes("dutch")) ||
                                        (selectedLang === "fr" && trackLang.includes("french"));

                    }
                }

                publication.setSubscribed(shouldSubscribe);
            };

            // Event handlers
            const handleTrackSubscribed = (track, publication, participant) => {
                setTracks(prev => {
                    // Avoid duplicates
                    const exists = prev.some(t => t.track.sid === track.sid);
                    if (exists) return prev;
                    return [...prev, { track, publication, participant }];
                });
            };

            const handleTrackUnsubscribed = (track, _, participant) => {
                setTracks(prev => {
                    const filtered = prev.filter(t => t.track.sid !== track.sid);
                    // Only update if something was actually removed
                    return filtered.length !== prev.length ? filtered : prev;
                });
            };

            const handleParticipantConnected = (participant) => {
            };

            const handleParticipantDisconnected = (participant) => {
                setTracks(prev => {
                    const filtered = prev.filter(t => t.participant.sid !== participant.sid);
                    // Only update if something was actually removed
                    return filtered.length !== prev.length ? filtered : prev;
                });
            };

            const handleDisconnected = (reason) => {
                setTracks([]);
                isConnectingRef.current = false;

                // Don't clean up room if it's just a temporary disconnection
                // LiveKit will automatically attempt to reconnect
                if (reason !== 'CLIENT_INITIATED') {
                }
            };

            const handleConnectionStateChanged = (state) => {
                if (state === 'connected') {
                    isConnectingRef.current = false;
                } else if (state === 'reconnecting') {
                } else if (state === 'disconnected') {
                    isConnectingRef.current = false;
                } else if (state === 'failed') {
                    isConnectingRef.current = false;
                }
            };

            // Attach event listeners
            room.on(RoomEvent.TrackPublished, handleTrackPublished);
            room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
            room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
            room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
            room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
            room.on(RoomEvent.Disconnected, handleDisconnected);
            room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
            
            // Handle successful reconnections
            room.on(RoomEvent.Reconnected, () => {
                isConnectingRef.current = false;
            });

            roomRef.current = room;
            return room;
        };

        const connectToRoom = async () => {

            if (!connectionState.url || !connectionState.token || status !== "started" || !autoplay) {
                return;
            }

            if (roomRef.current?.state === 'connected' || roomRef.current?.state === 'reconnecting' || isConnectingRef.current) {
                return; // Already connected or connecting
            }

            isConnectingRef.current = true;
            const roomInstance = initializeRoom();

            try {
                await roomInstance.connect(connectionState.url, connectionState.token);
            } catch (error) {
                isConnectingRef.current = false;
                toast.error("Failed to connect to meeting room. Please try again.");
                // Clear connection state and redirect to home
                setConnectionState({ url: null, token: null, room: null, live_token: null });
                setTimeout(() => {
                    navigate('/');
                }, 2000); // Give user time to see the error message
            }
        };

        // Attempt connection
        connectToRoom();

        // Update audio subscriptions when language changes
        if (roomRef.current && roomRef.current.state === 'connected') {
            roomRef.current.participants.forEach(participant => {
                participant.audioTracks.forEach(publication => {
                    const trackName = publication.name || publication.trackName || '';
                    if (participant.identity === `whip_streamer_${id}`) {
                        // Keep OBS audio subscribed
                        return;
                    }
                    // Re-evaluate language-specific audio
                    const trackLang = trackName.toLowerCase();
                    const selectedLang = lang.toLowerCase();
                    // Match track names like "dutch_audio" with "nl", "french_audio" with "fr"
                    const shouldSubscribe = trackLang === selectedLang ||
                                          trackLang.startsWith(selectedLang) ||
                                          (selectedLang === "nl" && trackLang.includes("dutch")) ||
                                          (selectedLang === "fr" && trackLang.includes("french"));
                    if (publication.isSubscribed !== shouldSubscribe) {
                        publication.setSubscribed(shouldSubscribe);
                    }
                });
            });
        }

        // Cleanup function
        return () => {
            if (roomRef.current) {
                roomRef.current.disconnect();
                roomRef.current = null;
            }
            room = null;
            isConnectingRef.current = false;
        };
    }, [connectionState.url, connectionState.token, status, autoplay, lang]);

    // Track rendering with debug logging
    useEffect(() => {
        
        if (tracks.length === 0) {
            setMainPlaceholder(true);
            setSubPlaceholder(true);
            return;
        }

        // Track what we have
        const videoTracks = {
            main: null,
            screenshare: null,
        };
        const audioTracks = {
            fr: null,
            nl: null,
            obs: null, // OBS audio track
        };

        // Categorize tracks
        tracks.forEach(({ track, publication, participant }) => {
            
            if (track.kind === Track.Kind.Video) {
                // Check all possible ways the track name could be set
                const trackName = publication.trackName || publication.name || '';
                
                if (participant.identity === `whip_streamer_${id}`) {
                    videoTracks.screenshare = track;
                } else if (trackName === "main_camera") {
                    videoTracks.main = track;
                }
            } else if (track.kind === Track.Kind.Audio) {
                // Check all possible ways the track name could be set
                const trackName = publication.trackName || publication.name || '';
                
                if (participant.identity === `whip_streamer_${id}`) {
                    // This is the OBS audio track
                    audioTracks.obs = track;
                } else if (trackName === "dutch_audio") {
                    audioTracks.nl = track;
                } else if (trackName === "french_audio") {
                    audioTracks.fr = track;
                }
            }
        });

        // Debug what we found

        // Handle main video player (only show screenshare/OBS, no fallback)
        if (mainPlayer.current && videoTracks.screenshare) {
            const streamTracks = [videoTracks.screenshare.mediaStreamTrack];

            // Always add OBS audio if available to the video
            if (audioTracks.obs) {
                streamTracks.push(audioTracks.obs.mediaStreamTrack);
            }

            const stream = new MediaStream(streamTracks);

            // Remove old listener before adding new one
            if (mainPlayerHandlerRef.current) {
                mainPlayer.current.removeEventListener('playing', mainPlayerHandlerRef.current);
            }

            mainPlayer.current.srcObject = stream;

            // Show "building connection" message while video loads
            setIsVideoConnecting(true);

            // Wait for video to actually render before hiding placeholder
            const handlePlaying = () => {
                setMainPlaceholder(false);
                setIsVideoConnecting(false);
                if (mainPlayer.current && mainPlayerHandlerRef.current) {
                    mainPlayer.current.removeEventListener('playing', mainPlayerHandlerRef.current);
                    mainPlayerHandlerRef.current = null;
                }
            };

            // Store handler in ref for cleanup
            mainPlayerHandlerRef.current = handlePlaying;
            mainPlayer.current.addEventListener('playing', handlePlaying);

            if (autoplay) {
                mainPlayer.current.play().catch(e => {
                    // Don't disconnect the room just because autoplay failed
                    // setAutoplay(false);
                    if (!open) {
                        setOpen(true);
                    }
                });
            }
        } else if (mainPlayer.current) {
            mainPlayer.current.srcObject = null;
            setMainPlaceholder(true);
            setIsVideoConnecting(false);
        }
        
        // Handle dedicated audio element for language tracks (Dutch or French)
        if (audioPlayer.current) {
            const selectedAudio = lang === "FR" ? audioTracks.fr : audioTracks.nl;
            if (selectedAudio) {
                const audioStream = new MediaStream([selectedAudio.mediaStreamTrack]);
                audioPlayer.current.srcObject = audioStream;
                
                if (autoplay) {
                    audioPlayer.current.play().catch(e => {
                        // Audio autoplay might be blocked, show modal
                        if (!open) {
                            setOpen(true);
                        }
                    });
                } else {
                    // If autoplay is false but we have language audio tracks, show the modal
                    if (!open) {
                        setOpen(true);
                    }
                }
            } else {
                if (audioPlayer.current.srcObject) {
                    audioPlayer.current.srcObject = null;
                }
            }
        }

        // Handle sub video player (always show main camera when available)
        if (subPlayer.current && videoTracks.main) {
            const stream = new MediaStream([videoTracks.main.mediaStreamTrack]);

            // Remove old listener before adding new one
            if (subPlayerHandlerRef.current) {
                subPlayer.current.removeEventListener('playing', subPlayerHandlerRef.current);
            }

            subPlayer.current.srcObject = stream;

            // Show "building connection" message while video loads
            setIsSubVideoConnecting(true);

            // Wait for video to actually render before hiding placeholder
            const handlePlaying = () => {
                setSubPlaceholder(false);
                setIsSubVideoConnecting(false);
                if (subPlayer.current && subPlayerHandlerRef.current) {
                    subPlayer.current.removeEventListener('playing', subPlayerHandlerRef.current);
                    subPlayerHandlerRef.current = null;
                }
            };

            // Store handler in ref for cleanup
            subPlayerHandlerRef.current = handlePlaying;
            subPlayer.current.addEventListener('playing', handlePlaying);

            if (autoplay) {
                subPlayer.current.play().catch(e => {
                    // Don't disconnect the room just because autoplay failed
                    if (!open) {
                        // setAutoplay(false);
                        setOpen(true);
                    }
                });
            }
        } else if (subPlayer.current) {
            subPlayer.current.srcObject = null;
            setSubPlaceholder(true);
            setIsSubVideoConnecting(false);
        }

        // Audio tracks are now handled by the dedicated audio element

        // Cleanup event listeners
        return () => {
            // Remove any pending event listeners to prevent memory leaks
            if (mainPlayer.current && mainPlayerHandlerRef.current) {
                mainPlayer.current.removeEventListener('playing', mainPlayerHandlerRef.current);
                mainPlayerHandlerRef.current = null;
            }
            if (subPlayer.current && subPlayerHandlerRef.current) {
                subPlayer.current.removeEventListener('playing', subPlayerHandlerRef.current);
                subPlayerHandlerRef.current = null;
            }
        };
    }, [tracks, lang, autoplay, subPlayerVisible]);

    // Visibility-based optimization for sub player
    useEffect(() => {
        if (!subPlayer.current || !roomRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const isVisible = entries[0].isIntersecting;
                setSubPlayerVisible(isVisible);

                // Find main camera track publication for bandwidth optimization
                if (roomRef.current.state === 'connected') {
                    roomRef.current.participants.forEach(participant => {
                        participant.videoTracks.forEach(publication => {
                            const trackName = publication.name || publication.trackName || '';

                            // Only manage main camera track visibility, not OBS
                            if (trackName === "main_camera" ||
                                (participant.identity !== `whip_streamer_${id}` && publication.kind === 'video')) {

                                if (publication.isSubscribed !== isVisible) {
                                    publication.setSubscribed(isVisible);
                                }
                            }
                        });
                    });
                }
            },
            { threshold: 0.1 } // Trigger when 10% visible
        );

        observer.observe(subPlayer.current);

        return () => observer.disconnect();
    }, [id]);

    function sendNewChat() {
        if (message !== "") {
            socketprovider.socket.emit('chat', { id: id, message: message });
            setMessage("");
        }
    }

    function vote(choice) {
        VotingService.vote(id, choice, voting.id)
            .then(response => {
                setVotingSelect(response.data.response);
            }
            )
            .catch(err => {
            }
            );
    }

    function removeVote() {
        VotingService.removeVote(id, votingSelect, voting.id)
            .then(response => {
                setVotingSelect("");
            }
            )
            .catch(err => {
            }
            );
    }

    function switchLang(slang) {
        if (slang != lang) {
            setLang(slang);
            // Update URL without refresh
            const newUrl = `/meeting/${id}?lang=${slang}`;
            window.history.pushState({ path: newUrl }, '', newUrl);
        }
    }

    function reloadAutoplay() {
        // User clicked button - that's explicit permission, no need to check canAutoPlay
        if (!autoplay) {
            setAutoplay(true);
            setOpen(false);

            // Try to play all media elements that have srcObject
            // Use setTimeout to ensure autoplay state has updated and triggered connection
            setTimeout(() => {
                [mainPlayer, subPlayer, audioPlayer].forEach(playerRef => {
                    if (playerRef.current && playerRef.current.srcObject) {
                        playerRef.current.play().catch(e => {
                            console.log('Video play failed:', e);
                        });
                    }
                });
            }, 100);
        }
    }

    // Show loading while checking connection
    if (isCheckingConnection || !isValidConnection) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-white text-lg">Loading meeting...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {status == "started"
                ?
                <div className="flex flex-col lg:flex-row h-screen max-h-screen overflow-hidden">
                    {/* Main Video Section */}
                    <div className="flex-1 lg:flex-[4] relative bg-black">
                        <div className="w-full h-full flex items-center justify-center">
                            <video
                                className={`w-full h-full object-contain ${mainPlaceholder === true ? "hidden" : "block"}`}
                                autoPlay
                                playsInline
                                ref={mainPlayer}
                            />
                            {mainPlaceholder === true && (
                                <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                                    <div className="text-center px-4">
                                        <AssembloLogoHor className="h-16 sm:h-20 lg:h-24 mx-auto mb-4" />
                                        <h1 className="text-white text-base sm:text-lg lg:text-xl font-bold mb-2">
                                            {t("meeting.busy.biepboepbap")}
                                        </h1>
                                        <h3 className="text-white text-sm sm:text-base font-semibold mb-6">
                                            {isVideoConnecting
                                                ? t("meeting.busy.streamstarting")
                                                : t("meeting.busy.backsoon")
                                            }
                                        </h3>
                                        <Spinner />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="w-full lg:w-80 xl:w-96 bg-gradient-to-br from-[#392b5f] to-[#4e2071] flex flex-col min-h-0 overflow-hidden">
                        {/* Hidden audio element for language tracks */}
                        <audio 
                            ref={audioPlayer}
                            autoPlay
                            className="hidden"
                            playsInline
                        />
                        
                        {/* Header Section */}
                        <div className="flex-shrink-0">
                            <div className="p-4 lg:p-6">
                                <AssembloLogoHor className="h-8 sm:h-10 lg:h-12 mx-auto mb-4" />
                                
                                {/* Language Toggle */}
                                <div className="mb-4">
                                    <Toggle 
                                        options={[
                                            { label: "NL", value: () => switchLang("NL") }, 
                                            { label: "FR", value: () => switchLang("FR") }
                                        ]} 
                                        defaultChecked={lang == "FR"}
                                    />
                                </div>

                                {/* Sub Video Player */}
                                <div className="relative">
                                    <video
                                        className={`w-full aspect-video rounded-lg shadow-lg ${subPlaceholder === true ? "hidden" : "block"}`}
                                        autoPlay
                                        muted
                                        playsInline
                                        ref={subPlayer}
                                    />
                                    {subPlaceholder === true && (
                                        <div className="w-full aspect-video rounded-lg shadow-lg bg-gray-800 flex items-center justify-center">
                                            <div className="text-center px-3">
                                                <h1 className="text-white text-sm font-bold mb-1">
                                                    {t("meeting.busy.biepboepbap")}
                                                </h1>
                                                <h3 className="text-white text-xs font-semibold">
                                                    {isSubVideoConnecting
                                                        ? t("meeting.busy.streamstarting")
                                                        : t("meeting.busy.backsoon")
                                                    }
                                                </h3>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Chat Section - Takes remaining space */}
                        <div className="flex-1 min-h-0 p-4 lg:p-6 pt-0 overflow-hidden">
                            <ChatComponent
                                chat={chat}
                                message={message}
                                setMessage={setMessage}
                                sendNewChat={sendNewChat}
                            />
                        </div>
                    </div>

                    {/* Voting Overlay - Mobile optimized */}
                    {voting && voting.live && (
                        <div className="fixed inset-x-0 bottom-0 z-30 flex justify-center p-4 pointer-events-none">
                            {votingSelect === "" ? (
                                <div className="w-full max-w-md bg-white rounded-lg shadow-xl overflow-hidden pointer-events-auto">
                                    <div className="grid grid-cols-2 gap-0">
                                        <button
                                            className="bg-lime-400 hover:bg-lime-500 py-4 px-6 text-white font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-lime-400 focus:ring-offset-2"
                                            onClick={() => vote("yes")}
                                        >
                                            {t('meeting.start.voting.votes.yes').toUpperCase()}
                                        </button>
                                        <button
                                            className="bg-red-500 hover:bg-red-600 py-4 px-6 text-white font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                            onClick={() => vote("no")}
                                        >
                                            {t('meeting.start.voting.votes.no').toUpperCase()}
                                        </button>
                                    </div>
                                    <button
                                        className="w-full bg-slate-600 hover:bg-slate-700 py-4 px-6 text-white font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-slate-600 focus:ring-offset-2"
                                        onClick={() => vote("abstain")}
                                    >
                                        {t('meeting.start.voting.votes.abstain').toUpperCase()}
                                    </button>
                                </div>
                            ) : (
                                <div className="w-full max-w-md bg-white rounded-lg shadow-xl p-6 pointer-events-auto">
                                    <div className="mb-4">
                                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                                            {t('meeting.start.voting.title')}
                                        </h3>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-sm text-gray-600">
                                                    {t('meeting.start.voting.yourvote')}
                                                </span>
                                                {votingSelect === "yes" && (
                                                    <span className="inline-flex items-center rounded-full bg-lime-400 px-3 py-1 text-sm font-medium text-white">
                                                        {t('meeting.start.voting.votes.yes')}
                                                    </span>
                                                )}
                                                {votingSelect === "no" && (
                                                    <span className="inline-flex items-center rounded-full bg-red-500 px-3 py-1 text-sm font-medium text-white">
                                                        {t('meeting.start.voting.votes.no')}
                                                    </span>
                                                )}
                                                {votingSelect === "abstain" && (
                                                    <span className="inline-flex items-center rounded-full bg-slate-600 px-3 py-1 text-sm font-medium text-white">
                                                        {t('meeting.start.voting.votes.abstain')}
                                                    </span>
                                                )}
                                            </div>
                                            <button 
                                                className="text-xs text-indigo-600 hover:text-indigo-800 underline focus:outline-none" 
                                                onClick={() => removeVote()}
                                            >
                                                {t('meeting.start.voting.change')}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Responsive Voting Results */}
                                    <div className="space-y-3">
                                        {/* Yes votes */}
                                        <div className="flex items-center space-x-3">
                                            <div className="w-16 h-8 bg-lime-400 rounded flex items-center justify-center">
                                                <span className="text-white text-xs font-semibold">
                                                    {globalVotes["yes"] > 0 ? `${Math.round((globalVotes["yes"] / (globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"])) * 100)}%` : '0%'}
                                                </span>
                                            </div>
                                            <div className="flex-1 bg-gray-200 rounded-full h-3">
                                                <div 
                                                    className="bg-lime-400 h-3 rounded-full transition-all duration-500" 
                                                    style={{ width: `${(globalVotes["yes"] / (globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"])) * 100}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-gray-600 w-12">
                                                {globalVotes["yes"]} {globalVotes["yes"] === 1 ? t('meeting.start.voting.vote') : t('meeting.start.voting.multivotes')}
                                            </span>
                                        </div>

                                        {/* No votes */}
                                        <div className="flex items-center space-x-3">
                                            <div className="w-16 h-8 bg-red-500 rounded flex items-center justify-center">
                                                <span className="text-white text-xs font-semibold">
                                                    {globalVotes["no"] > 0 ? `${Math.round((globalVotes["no"] / (globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"])) * 100)}%` : '0%'}
                                                </span>
                                            </div>
                                            <div className="flex-1 bg-gray-200 rounded-full h-3">
                                                <div 
                                                    className="bg-red-500 h-3 rounded-full transition-all duration-500" 
                                                    style={{ width: `${(globalVotes["no"] / (globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"])) * 100}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-gray-600 w-12">
                                                {globalVotes["no"]} {globalVotes["no"] === 1 ? t('meeting.start.voting.vote') : t('meeting.start.voting.multivotes')}
                                            </span>
                                        </div>

                                        {/* Abstain votes */}
                                        <div className="flex items-center space-x-3">
                                            <div className="w-16 h-8 bg-slate-600 rounded flex items-center justify-center">
                                                <span className="text-white text-xs font-semibold">
                                                    {globalVotes["abstain"] > 0 ? `${Math.round((globalVotes["abstain"] / (globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"])) * 100)}%` : '0%'}
                                                </span>
                                            </div>
                                            <div className="flex-1 bg-gray-200 rounded-full h-3">
                                                <div 
                                                    className="bg-slate-600 h-3 rounded-full transition-all duration-500" 
                                                    style={{ width: `${(globalVotes["abstain"] / (globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"])) * 100}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-gray-600 w-12">
                                                {globalVotes["abstain"]} {globalVotes["abstain"] === 1 ? t('meeting.start.voting.vote') : t('meeting.start.voting.multivotes')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
                : status == "ended"
                    ? <EndedComponent meeting_id={id} />
                    : <WaitingComponent date={meeting.date} lang={lang} />
            }
            {!autoplay && status == "started" ? (
                <Transition.Root show={open} as={Fragment}>
                    <Dialog as="div" className="relative z-10" onClose={() => {}}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>

                        <div className="fixed inset-0 z-10 overflow-y-auto">
                            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-300"
                                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                                    leave="ease-in duration-200"
                                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                >
                                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                        <div>
                                            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                                <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                                            </div>
                                            <div className="mt-3 text-center sm:mt-5">
                                                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                                    {t("meeting.busy.ready")}
                                                </Dialog.Title>
                                                <div className="mt-2">
                                                    <p className="text-sm text-gray-500">
                                                        {t("meeting.busy.readysub")}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-5 sm:mt-6">
                                            <button
                                                type="button"
                                                className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                                onClick={() => reloadAutoplay()}
                                            >
                                                {t("meeting.busy.readybutton")}
                                            </button>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition.Root>
            ) : null}

        </>

    )
}

// Wrapper to disable Strict Mode for LiveKit compatibility
export default function MeetingView() {
    return <MeetingViewContent />;
}